// src/redux/AllPropertiesSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import i18n from "../../i18n/i18n";

const BASE_URL = process.env.REACT_APP_BASE_URL;

// Async thunks
export const fetchPurposes = createAsyncThunk(
  "properties/fetchPurposes",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/api/property/purposes`, {
        headers: {
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return { data: response.data.data };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to load");
    }
  }
);

export const fetchTypes = createAsyncThunk(
  "properties/fetchTypes",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/api/property/types`, {
        headers: {
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return { data: response.data.data };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to load");
    }
  }
);

export const fetchLevels = createAsyncThunk(
  "properties/fetchLevels",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/api/property/levels`, {
        headers: {
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return { data: response.data.data };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to load");
    }
  }
);

export const fetchPropertCategories = createAsyncThunk(
  "properties/fetchPropertCategories",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/api/property/categories`, {
        headers: {
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return { data: response.data.data };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to load");
    }
  }
);

export const fetchPropertCategoriesChilds = createAsyncThunk(
  "properties/fetchPropertCategoriesChilds",
  async ({ id, levelIndex }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/api/property/categories?parent_id=${id}`, {
        headers: {
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return { data: response.data.data, levelIndex };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to load");
    }
  }
);

// Initial state
const initialState = {
  purposes: [],
  types: [],
  levels: [],
  property_categories: [],
  dynamic_categories: [],
  isLoading: false,
  success: null,
  error: null,
};

// Slice
const PropertyApisSlice = createSlice({
  name: "properties_api",
  initialState,
  reducers: {
    resetDynamicCategories: (state, action) => {
      const levelIndex = action.payload;
      // Clears all children levels from the selected index onwards
      state.dynamic_categories = state.dynamic_categories.slice(0, levelIndex);
    },
    clearState: (state) => {
      state.isLoading = false;
      state.success = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Purposes
      .addCase(fetchPurposes.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchPurposes.fulfilled, (state, action) => {
        state.isLoading = false;
        state.purposes = action.payload.data;
      })
      .addCase(fetchPurposes.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Something went wrong";
      })
      // Types
      .addCase(fetchTypes.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchTypes.fulfilled, (state, action) => {
        state.isLoading = false;
        state.types = action.payload.data;
      })
      .addCase(fetchTypes.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Something went wrong";
      })
      // Levels
      .addCase(fetchLevels.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchLevels.fulfilled, (state, action) => {
        state.isLoading = false;
        state.levels = action.payload.data;
      })
      .addCase(fetchLevels.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Something went wrong";
      })
      // Main Categories
      .addCase(fetchPropertCategories.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchPropertCategories.fulfilled, (state, action) => {
        state.isLoading = false;
        state.property_categories = action.payload.data;
      })
      .addCase(fetchPropertCategories.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Something went wrong";
      })
      // Dynamic Children
      .addCase(fetchPropertCategoriesChilds.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPropertCategoriesChilds.fulfilled, (state, action) => {
        state.isLoading = false;
        const { data, levelIndex } = action.payload;
        if (data && data.length > 0) {
          // Insert at exact level, replacing anything there
          state.dynamic_categories[levelIndex] = data;
          // Clear any deeper levels that are now stale
          state.dynamic_categories = state.dynamic_categories.slice(0, levelIndex + 1);
        }
      })
      .addCase(fetchPropertCategoriesChilds.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Something went wrong";
      });
  },
});

export const { clearState, resetDynamicCategories } = PropertyApisSlice.actions;
export default PropertyApisSlice.reducer;